import { DndContext, type DragEndEvent, useDraggable } from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import { Badge } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Card, CardContent } from '@neo/primitives/card'
import { Tooltip } from '@neo/primitives/tooltip'
// @ts-expect-error no types for GIF
import recordingGif from '@shared/assets/icons/ic-recording.gif'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { useState } from 'react'
import { getFormattedContactName, isOneOfMonitoringStates } from 'v2/pages/conversations/utils'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { ElapsedTimer } from 'v2/shared/components/elapsed-timer'
import { usePhoneStore } from 'v2/state/phone'
import { ConvChannels } from 'v2/types/api/conversations'
import { CallDirection, ParticipantState } from 'v2/types/call'
import { TransferTabs } from '../transfer-modal/transfer-tabs'
import { CallProvider, useCallContext } from './call-context'
import { CallControls } from './call-controls'
import { CallHeader } from './call-header'
import { CallTitle } from './call-title'
import { MonitoringBannerAndControls } from './monitoring/banner-and-controls'
import { ParticipantSection } from './participant-section'

interface Position {
  x: number
  y: number
}

interface DraggableContentProps {
  position: Position
}

const DraggableContent = ({ position }: DraggableContentProps) => {
  const [isMinimized, setIsMinimized] = useState(false)
  const {
    handleShowTransferModal,
    isIncomingCallRinging,
    showTransferModal,
    handleToggleRecording,
    isToggleRecordingPending,
  } = useCallContext()

  const callPickedup = usePhoneStore((state) => state.callPickedup)
  const activeCall = usePhoneStore((state) => state.activeCall)
  const isCallActive = usePhoneStore((state) => state.isCallActive)
  const isRecordingPaused = usePhoneStore((state) => state.isRecordingPaused)
  const isOnHold = usePhoneStore((state) => state.isOnHold)
  const isSecondaryParticipant = usePhoneStore((state) => state.isSecondaryParticipant)
  const monitoringState = usePhoneStore((state) => state.monitoringState)

  const { callChildConversation } = usePhoneStore((state) => state.callConversation)
  const isActiveMonitoringState = isOneOfMonitoringStates(monitoringState)

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'incoming-call-popup',
  })
  const { data: profile } = useProfileQuery()

  const isRecordingEnabled =
    callPickedup &&
    (activeCall?.direction === CallDirection.INBOUND
      ? profile?.organizations[0].call_recording_inbound
      : profile?.organizations[0].call_recording_outbound)

  const allowPauseResume =
    isRecordingEnabled &&
    profile?.organizations[0].allow_recording_pause_play &&
    !isSecondaryParticipant

  const style = {
    position: 'fixed' as const,
    zIndex: 50,
    left: position.x,
    top: position.y,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  }

  const participants = usePhoneStore((state) => state.participants)
  const mergedParticipants = participants.filter((p) => p.state === ParticipantState.MERGED)
  const activeParticipant = participants.find(
    (p) =>
      p.state === ParticipantState.CONNECTING ||
      p.state === ParticipantState.CONNECTED ||
      p.state === ParticipantState.CONNECTING_COLD_TRANSFER,
  )
  const aliasName = getFormattedContactName(activeCall?.extraHeaders?.aliasName || '')

  return (
    <Card ref={setNodeRef} style={style} className="w-80 shadow-lg overflow-hidden px-4 pb-3">
      <CallHeader
        isMinimized={isMinimized}
        setIsMinimized={setIsMinimized}
        dragAttributes={attributes as unknown as Record<string, unknown>}
        dragListeners={listeners as unknown as Record<string, unknown>}
      />

      <CardContent className="p-0">
        <div>
          <div className="flex flex-col gap-2">
            <div className="text-xs-400 text-tertiary flex items-center justify-between">
              <div className="flex gap-1 items-center overflow-hidden">
                {aliasName && (
                  <>
                    <span className="shrink-0">
                      {activeCall?.direction === CallDirection.INBOUND
                        ? 'Call to'
                        : 'Calling from'}{' '}
                    </span>
                    <span className="text-xs-600 truncate">{aliasName}</span>
                  </>
                )}
              </div>
              {isRecordingEnabled && callPickedup && (
                <div className="flex items-center justify-center gap-1 group shrink-0">
                  <Tooltip content={`Recording is ${isRecordingPaused ? 'paused' : 'in progress'}`}>
                    <img
                      src={recordingGif}
                      alt="recording"
                      className={cn('size-4', isRecordingPaused && 'grayscale brightness-125')}
                    />
                  </Tooltip>
                  {allowPauseResume && (
                    <button
                      type="button"
                      className={cn(
                        'text-xxs-600 text-destructive underline uppercase hidden group-hover:block',
                        isRecordingPaused && 'text-tertiary',
                      )}
                      disabled={isToggleRecordingPending}
                      onClick={handleToggleRecording}
                    >
                      {isRecordingPaused ? 'Resume rec' : 'Pause rec'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {!isMinimized && <CallTitle isMinimized={isMinimized} />}

            <div className={cn('flex items-center gap-2 flex-wrap ml-5', isMinimized && 'ml-0')}>
              {isMinimized && <CallTitle isMinimized={isMinimized} />}
              <div className={cn('flex flex-col w-full', isMinimized && 'w-auto')}>
                <span
                  className="text-p-600 flex items-start gap-1 justify-between"
                  data-testid="contact-name"
                >
                  <span>
                    {getFormattedContactName(
                      activeCall?.contactName || activeCall?.callWith || 'Anonymous',
                    )}
                    {mergedParticipants.length > 0 && (
                      <span className="text-tertiary text-p-400"> (customer)</span>
                    )}
                    {mergedParticipants.length > 0 && ', '}
                    {mergedParticipants.length > 0 && (
                      <>
                        {mergedParticipants
                          .map((p) => getFormattedContactName(p.identifier))
                          .join(', ')}
                        &nbsp;and you
                      </>
                    )}
                  </span>
                  {!isMinimized && (
                    <Badge
                      variant="warning"
                      className={cn(isOnHold ? '' : 'invisible', 'shrink-0')}
                    >
                      On Hold
                    </Badge>
                  )}
                </span>
                {!isCallActive &&
                  activeCall?.contactName &&
                  !isValidPhoneNumber(activeCall.contactName) && (
                    <span className="text-p-400 text-tertiary" data-testid="contact-number">
                      {activeCall.contactName === 'No Caller ID'
                        ? ''
                        : getFormattedContactName(activeCall?.callWith || '')}
                    </span>
                  )}
                {isCallActive && (
                  <span>
                    {callChildConversation?.start_at ? (
                      <ElapsedTimer
                        startTime={callChildConversation.start_at}
                        format="mm:ss"
                        className="text-p-400 text-tertiary"
                      />
                    ) : (
                      <span className="text-p-400 text-tertiary">Connecting...</span>
                    )}
                  </span>
                )}
              </div>
              <div
                className={cn(
                  'flex-1 basis-full',
                  isMinimized && 'flex-none ml-auto',
                  isIncomingCallRinging && 'w-full',
                )}
              >
                <CallControls isMinimized={isMinimized} />
              </div>
            </div>

            {/* Feature: CALL_MONITORING */}
            {isActiveMonitoringState && <MonitoringBannerAndControls />}

            {activeParticipant && !isMinimized && (
              <ParticipantSection participant={activeParticipant} />
            )}
          </div>

          {showTransferModal && !isMinimized && (
            <div className="w-full mt-4">
              <TransferTabs
                channel={ConvChannels.CALL}
                onTransferSuccess={({ name, identifier, type, participantType }) => {
                  handleShowTransferModal(false)
                  usePhoneStore.getState().addParticipant({
                    member: identifier,
                    state:
                      type === 'cold'
                        ? ParticipantState.CONNECTING_COLD_TRANSFER
                        : ParticipantState.CONNECTING,
                    identifier: name,
                    startAt: new Date().toString(),
                    isOnHold: false,
                    participantType,
                  })
                  usePhoneStore.getState().setOnHold(true)
                }}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export const FloatingCallWidget = () => {
  const [position, setPosition] = useState<Position>({
    x: window.innerWidth - 356,
    y: 16,
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { delta } = event
    if (!delta) return

    setPosition((currentPosition) => ({
      x: currentPosition.x + delta.x,
      y: currentPosition.y + delta.y,
    }))
  }

  return (
    <CallProvider>
      <DndContext modifiers={[restrictToWindowEdges]} onDragEnd={handleDragEnd}>
        <DraggableContent position={position} />
      </DndContext>
    </CallProvider>
  )
}
